import { c as _c } from "react/compiler-runtime";
import { getRechartsNiceTickValues as getNiceTickValues, type RechartsNumberDomain as NumberDomain, type RechartsCategoricalDomain, useRechartsXAxisDomain as useXAxisDomain, useRechartsYAxisDomain as useYAxisDomain } from '@precis-digital/kurama/recharts';
import { type Dispatch, type RefObject, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getColumnDataKey } from './components/table/utils';
import { tickCountBreakpoints, ZOOM_OVERLAY_MIN_CHANGE_FACTOR } from './constants';
import { useDeepCompareMemoize } from './hooks/generics';
import type { AxisIdProps, ChartCategoricalDomain as CategoricalDomain, ChartAxisIdsAndDomains, ChartCardState, ChartCardStateAction, ChartCardTableContainerProps, ChartZoomedIn, DimensionAxisType, DimensionOrMetricDomain, DrawingElementProps, ElementsDescriptionsMap, FirstOrThirdAxisDetails, MetricDomain, SecondAxisDetails, TotalsAndRangeData, ValidatedChartCardProps } from './internalTypes';
import type { ChartCardChartDefinition, ChartCardGridSize, ChartDataRow, DefaultDrawingInnerProps, DrawingColorMaps, LayoutType } from './types';
import { createCartesianDrawingElementsProps, createPolarDrawingElementsProps, createScatterDrawingElementsProps } from './utils/chart';
import { aggregateMetric, aggregateMetricRespectingRecalculate } from './utils/data';
import { assertNever, errorLocal, objectEntries } from './utils/generics';
import { stateReducerCheckCardTypeAction, stateReducerInitChartAction, stateReducerInitTableAction, stateReducerResetZoomingAction, stateReducerSelectionAction, stateReducerUpdateDateRangeAction, stateReducerUpdateSelectionsAction, stateReducerZoomInAction } from './utils/state';
export const usePrepareTotalsAndRangeData = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  data,
  metricsMap,
  columns
}: Pick<ChartCardTableContainerProps<DataKey, DataRow>, 'data' | 'metricsMap' | 'columns'>): TotalsAndRangeData<DataKey> => {
  return columns.reduce<TotalsAndRangeData<DataKey>>((acc, column) => {
    const dataKey = getColumnDataKey<DataKey, DataRow>(column);
    if (dataKey == null) {
      return acc;
    }
    const {
      totals,
      min,
      max
    } = acc;
    const metric = metricsMap[dataKey];
    if (metric == null) {
      totals[dataKey] = undefined;
      min[dataKey] = undefined;
      max[dataKey] = undefined;
      return acc;
    }
    totals[dataKey] = aggregateMetricRespectingRecalculate(data, metric, metricsMap) ?? 0;
    min[dataKey] = aggregateMetric(data, metric, 'min') ?? 0;
    max[dataKey] = aggregateMetric(data, metric, 'max') ?? 0;
    return acc;
  }, {
    totals: {},
    min: {},
    max: {}
  });
};
export const chartCardStateReducer = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(state: ChartCardState<DataKey, DataRow>, action: ChartCardStateAction<DataKey, DataRow>): ChartCardState<DataKey, DataRow> => {
  const {
    type
  } = action;
  switch (type) {
    case 'checkCardType':
      return stateReducerCheckCardTypeAction(state, action);
    case 'initChart':
      return stateReducerInitChartAction(state, action);
    case 'initTable':
      return stateReducerInitTableAction(state, action);
    case 'updateSelections':
      return stateReducerUpdateSelectionsAction(state, action);
    case 'resetSelections':
      return stateReducerUpdateSelectionsAction(state, action);
    case 'updateDateRange':
      return stateReducerUpdateDateRangeAction(state, action);
    case 'resetZooming':
      return stateReducerResetZoomingAction(state, action);
    case 'zoomIn':
      return stateReducerZoomInAction(state, action);
    case 'multipleAdd':
    case 'multipleRemove':
    case 'multipleSet':
    case 'singleSet':
    case 'resetSelection':
      return stateReducerSelectionAction(state, action);
    default:
      assertNever(type);
  }
  return state;
};

/**
 * Keeps chart card reducer state in sync with validation: dispatches initChart/initTable
 * when validated props change (valid, invalid, or unchanged for loading).
 */
export const useInitChartCardStateFromValidatedProps = (validatedProps, dispatch) => {
  const $ = _c(18);
  const {
    result,
    card,
    choicesList,
    choicesMap,
    dimensionsMap
  } = validatedProps;
  let t0;
  if ($[0] !== card?.chart?.dimensionAxis || $[1] !== card?.columns || $[2] !== card?.performance?.dimensionAxis || $[3] !== choicesList || $[4] !== choicesMap || $[5] !== dimensionsMap || $[6] !== dispatch || $[7] !== result) {
    t0 = () => {
      bb2: switch (result) {
        case "validChart":
          {
            dispatch({
              type: "initChart",
              props: {
                result,
                choicesList,
                choicesMap,
                dimensionAxis: (card?.performance?.dimensionAxis ?? card?.chart?.dimensionAxis) as DimensionAxisType,
                dimensionsMap
              }
            });
            break bb2;
          }
        case "validTable":
          {
            dispatch({
              type: "initTable",
              props: {
                result,
                choicesList,
                choicesMap,
                columns: card?.columns,
                dimensionsMap
              }
            });
            break bb2;
          }
        case "invalidChart":
          {
            dispatch({
              type: "initChart",
              props: {
                result
              }
            });
            break bb2;
          }
        case "invalidTable":
          {
            dispatch({
              type: "initTable",
              props: {
                result
              }
            });
            break bb2;
          }
        case "loadingChart":
        case "loadingTable":
          {
            break bb2;
          }
        default:
          {
            assertNever(result);
          }
      }
    };
    $[0] = card?.chart?.dimensionAxis;
    $[1] = card?.columns;
    $[2] = card?.performance?.dimensionAxis;
    $[3] = choicesList;
    $[4] = choicesMap;
    $[5] = dimensionsMap;
    $[6] = dispatch;
    $[7] = result;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  const t1 = card?.chart?.dimensionAxis;
  const t2 = card?.performance?.dimensionAxis;
  const t3 = card?.columns;
  let t4;
  if ($[9] !== choicesList || $[10] !== choicesMap || $[11] !== dimensionsMap || $[12] !== dispatch || $[13] !== result || $[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = [result, dimensionsMap, choicesList, choicesMap, t1, t2, t3, dispatch];
    $[9] = choicesList;
    $[10] = choicesMap;
    $[11] = dimensionsMap;
    $[12] = dispatch;
    $[13] = result;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  useEffect(t0, t4);
};
interface ContainerSize {
  width: number;
  height: number;
}
export const useContainerSize = (ref: RefObject<HTMLElement | null>): ContainerSize => {
  const [size, setSize] = useState<ContainerSize>({
    width: ref.current?.offsetWidth ?? 0,
    height: ref.current?.offsetHeight ?? 0
  });
  useLayoutEffect(() => {
    if (ref.current == null) return;
    const updateSize = (): void => {
      if (ref.current != null) {
        setSize({
          width: ref.current.offsetWidth,
          height: ref.current.offsetHeight
        });
      }
    };
    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [ref]);
  return size;
};
export const useDrawingElementsProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  chart,
  dimensionAxisDetails,
  elementsDescriptionsMap,
  secondAxisDetails,
  respectColors,
  chartDrawing,
  drawingColorMaps
}: {
  chart: ChartCardChartDefinition<DataKey, DataRow>;
  dimensionAxisDetails: FirstOrThirdAxisDetails<DataKey, DataRow>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  secondAxisDetails: Array<SecondAxisDetails<DataKey, DataRow>>;
  respectColors: boolean;
  chartDrawing: DefaultDrawingInnerProps;
  drawingColorMaps: DrawingColorMaps;
}): DrawingElementProps[] => useMemo<DrawingElementProps[]>(() => {
  const drawingProps: DrawingElementProps[] = [];
  const {
    layout,
    view,
    type: chartType,
    otherType: otherChartType
  } = chart;
  if (view === 'polar') {
    createPolarDrawingElementsProps<DataKey, DataRow>({
      axisProps: chart,
      dimensionAxisDetails,
      secondAxisDetails,
      elementsDescriptionsMap,
      respectColors,
      chartDrawing,
      drawingColorMaps,
      drawingProps
    });
    return drawingProps;
  }
  if (chartType === 'scatter') {
    createScatterDrawingElementsProps<DataKey, DataRow>({
      axisProps: chart,
      elementsDescriptionsMap,
      dimensionAxisDetails,
      secondAxisDetails,
      respectColors,
      chartDrawing,
      drawingColorMaps,
      drawingProps
    });
    return drawingProps;
  }
  for (const [axisIndex, {
    chartType: detailsChartType,
    axis,
    axisType,
    dataKeys,
    choiceValues,
    otherSelected,
    selectedOthers,
    selectedValues,
    color,
    pattern,
    customProps
  }] of secondAxisDetails.entries()) {
    if (detailsChartType !== chartType && detailsChartType !== otherChartType) {
      errorLocal(`useDrawingElementsProps secondAxisDetails ${axisIndex + 1} chartType doesn't match axisProps`, {
        axisProps: chart,
        secondAxisDetails
      });
      continue;
    }
    const axisIdProps: AxisIdProps = layout === 'vertical' ? {
      xAxisId: axis
    } : {
      yAxisId: axis
    };
    createCartesianDrawingElementsProps<DataKey, DataRow>({
      chartType: detailsChartType,
      respectColors,
      color,
      pattern,
      customProps,
      axisIdProps,
      choiceValues,
      selectedValues,
      otherSelected,
      selectedOthers,
      dataKeys,
      axisType,
      elementsDescriptionsMap,
      chartDrawing,
      drawingColorMaps,
      drawingProps
    });
  }
  return drawingProps;
}, [chart, dimensionAxisDetails, secondAxisDetails, elementsDescriptionsMap, respectColors, chartDrawing, drawingColorMaps]);
const calculateNiceDomain = (domain: NumberDomain | RechartsCategoricalDomain | undefined, tickCount: number): NumberDomain | CategoricalDomain | undefined => {
  if (domain == null) {
    return undefined;
  }
  if (typeof domain[0] === 'string') {
    return domain as CategoricalDomain;
  }
  const [min, max] = domain as [number, number];
  if (min === max) {
    return [min, max];
  }
  const niceTicks = getNiceTickValues([min, max], tickCount);
  if (niceTicks.length === 0) {
    return [min, max];
  }
  return [niceTicks[0], niceTicks.at(-1) as number];
};
const useNiceXDomain = (axisId, zoomedIn, tickCount) => {
  const $ = _c(3);
  const dataDomain = useDeepCompareMemoize(useXAxisDomain(axisId));
  let t0;
  bb0: {
    if (axisId == null || dataDomain == null) {
      t0 = undefined;
      break bb0;
    }
    if (zoomedIn) {
      t0 = dataDomain as CategoricalDomain | NumberDomain;
      break bb0;
    }
    let t1;
    if ($[0] !== dataDomain || $[1] !== tickCount) {
      t1 = calculateNiceDomain(dataDomain, tickCount);
      $[0] = dataDomain;
      $[1] = tickCount;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = t1;
  }
  const niceDomain = t0;
  return useDeepCompareMemoize(niceDomain);
};
export const useNiceYDomain = (axisId, zoomedIn, tickCount) => {
  const $ = _c(3);
  const dataDomain = useDeepCompareMemoize(useYAxisDomain(axisId));
  let t0;
  bb0: {
    if (axisId == null || dataDomain == null) {
      t0 = undefined;
      break bb0;
    }
    if (zoomedIn) {
      t0 = dataDomain as CategoricalDomain | NumberDomain;
      break bb0;
    }
    let t1;
    if ($[0] !== dataDomain || $[1] !== tickCount) {
      t1 = calculateNiceDomain(dataDomain, tickCount);
      $[0] = dataDomain;
      $[1] = tickCount;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = t1;
  }
  const niceDomain = t0;
  return useDeepCompareMemoize(niceDomain);
};
export const useZoomDomainMinChange = domain => {
  let t0;
  bb0: {
    if (domain == null) {
      t0 = 0;
      break bb0;
    }
    if (typeof domain[0] !== "number" || typeof domain[1] !== "number") {
      t0 = 0;
      break bb0;
    }
    t0 = Math.abs(domain[0] - domain[1]) * ZOOM_OVERLAY_MIN_CHANGE_FACTOR;
  }
  return t0;
};
const useTickCounts = (width, height) => {
  const $ = _c(7);
  let t0;
  if ($[0] !== width) {
    t0 = objectEntries(tickCountBreakpoints.width).reduce((acc, t1) => {
      const [breakpoint, value] = t1;
      if (width > breakpoint) {
        return value;
      }
      return acc;
    }, 1);
    $[0] = width;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== height) {
    t1 = objectEntries(tickCountBreakpoints.height).reduce((acc_0, t2) => {
      const [breakpoint_0, value_0] = t2;
      if (height > breakpoint_0) {
        return value_0;
      }
      return acc_0;
    }, 1);
    $[2] = height;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      xTicks: t0,
      yTicks: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const useChartAxisIdsAndDomains = t0 => {
  const $ = _c(18);
  const {
    layout,
    width,
    height,
    zoomedIn,
    firstAxisId,
    secondAxisId,
    otherSecondAxisId
  } = t0;
  const {
    xTicks,
    yTicks
  } = useTickCounts(width, height);
  const isHorizontal = layout === "horizontal";
  const xAxisDomain = useNiceXDomain(isHorizontal ? firstAxisId : secondAxisId, zoomedIn === true || zoomedIn === (isHorizontal ? "first" : "second"), xTicks);
  const xOtherAxisDomain = useNiceXDomain(isHorizontal ? undefined : otherSecondAxisId, zoomedIn === true || zoomedIn === "second", xTicks);
  const yAxisDomain = useNiceYDomain(isHorizontal ? secondAxisId : firstAxisId, zoomedIn === true || zoomedIn === (isHorizontal ? "second" : "first"), yTicks);
  const yOtherAxisDomain = useNiceYDomain(isHorizontal ? otherSecondAxisId : undefined, zoomedIn === true || zoomedIn === "first", yTicks);
  let t1;
  const firstAxisDomain = isHorizontal ? xAxisDomain : yAxisDomain;
  const secondAxisDomain = (isHorizontal ? yAxisDomain : xAxisDomain) as MetricDomain;
  const otherSecondAxisDomain = (xOtherAxisDomain ?? yOtherAxisDomain) as MetricDomain;
  if (layout === "horizontal") {
    let t2;
    if ($[0] !== firstAxisDomain || $[1] !== firstAxisId || $[2] !== otherSecondAxisDomain || $[3] !== otherSecondAxisId || $[4] !== secondAxisDomain || $[5] !== secondAxisId || $[6] !== xTicks || $[7] !== yTicks) {
      t2 = {
        firstAxisDomain,
        secondAxisDomain,
        otherSecondAxisDomain,
        xAxisId: firstAxisId,
        xTicks,
        xAxisDomain: firstAxisDomain,
        xOtherAxisId: undefined,
        xOtherAxisDomain: undefined,
        yAxisId: secondAxisId,
        yTicks,
        yAxisDomain: secondAxisDomain,
        yOtherAxisId: otherSecondAxisId,
        yOtherAxisDomain: otherSecondAxisDomain
      };
      $[0] = firstAxisDomain;
      $[1] = firstAxisId;
      $[2] = otherSecondAxisDomain;
      $[3] = otherSecondAxisId;
      $[4] = secondAxisDomain;
      $[5] = secondAxisId;
      $[6] = xTicks;
      $[7] = yTicks;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = t2;
  } else {
    let t2;
    if ($[9] !== firstAxisDomain || $[10] !== firstAxisId || $[11] !== otherSecondAxisDomain || $[12] !== otherSecondAxisId || $[13] !== secondAxisDomain || $[14] !== secondAxisId || $[15] !== xTicks || $[16] !== yTicks) {
      t2 = {
        firstAxisDomain,
        secondAxisDomain,
        otherSecondAxisDomain,
        xAxisId: secondAxisId,
        xTicks,
        xAxisDomain: secondAxisDomain,
        xOtherAxisId: otherSecondAxisId,
        xOtherAxisDomain: otherSecondAxisDomain,
        yAxisId: firstAxisId,
        yTicks,
        yAxisDomain: firstAxisDomain,
        yOtherAxisId: undefined,
        yOtherAxisDomain: undefined
      };
      $[9] = firstAxisDomain;
      $[10] = firstAxisId;
      $[11] = otherSecondAxisDomain;
      $[12] = otherSecondAxisId;
      $[13] = secondAxisDomain;
      $[14] = secondAxisId;
      $[15] = xTicks;
      $[16] = yTicks;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    t1 = t2;
  }
  return useDeepCompareMemoize(t1);
};